import type { DocumentRecord, MembershipRole, SettableMemberStatus } from "@shyne/shared/api";
import type { TeamMember, TeamMemberDetail } from "@/repositories/team-members.repository.ts";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReadAllResponse, uuid } from "@/interfaces";
import {
  changeRoleAndStatus,
  deleteDocumentForUser,
  getTeamMember,
  getTeamMemberDetail,
  listUserDocuments,
  uploadDocumentForUser,
} from "./team-member-detail.api";

interface DetailState {
  detail: TeamMemberDetail | null;
  loading: boolean;
  error: string | null;
  uploadModalOpen: boolean;
  documents: DocumentRecord[];
  docLoading: boolean;
  docPage: number;
  docPageSize: number;
  docTotal: number;
}

const initialState: DetailState = {
  detail: null,
  loading: false,
  error: null,
  uploadModalOpen: false,
  documents: [],
  docLoading: false,
  docPage: 1,
  docPageSize: 10,
  docTotal: 0,
};

export const fetchUserDocuments = createAsyncThunk<
  ReadAllResponse<DocumentRecord>,
  { person: uuid; page: number; pageSize: number }
>(
  "teamMemberDetail/fetchDocs",
  async ({ person, page, pageSize }) => await listUserDocuments(person, page, pageSize)
);

export const fetchTeamMemberDetail = createAsyncThunk("teamMemberDetail/fetch", async (memberId: uuid) => {
  return await getTeamMemberDetail(memberId);
});

export const updateTeamMember = createAsyncThunk(
  "teamMemberDetail/update",
  async ({ member, role, status }: { member: TeamMember; role: MembershipRole; status: SettableMemberStatus | undefined }) => await changeRoleAndStatus(member, role, status)
);

export const refetchTeamMember = createAsyncThunk("teamMemberDetail/refetchMember", async (memberId: uuid) => await getTeamMember(memberId));

export const uploadUserDocument = createAsyncThunk(
  "teamMemberDetail/uploadDoc",
  async (payload: { person: uuid; file: File; expiresAt?: string | Date }) => {
    return await uploadDocumentForUser(payload.person, payload.file, payload.expiresAt);
  }
);

export const deleteUserDocument = createAsyncThunk("teamMemberDetail/deleteDoc", async (docId: uuid) => {
  await deleteDocumentForUser(docId);
  return docId;
});

const slice = createSlice({
  name: "teamMemberDetail",
  initialState,
  reducers: {
    clearDetail(state) {
      state.detail = null;
      state.documents = [];
      state.loading = false;
      state.error = null;
      state.docPage = 1;
      state.docPageSize = 10;
      state.docTotal = 0;
    },
    setUploadDocumentModalOpen: (state, action) => {
      state.uploadModalOpen = action.payload;
    },
    setDocPage(state, action: PayloadAction<number>) {
      state.docPage = action.payload;
    },
    setDocPageSize(state, action: PayloadAction<number>) {
      state.docPageSize = action.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchTeamMemberDetail.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchTeamMemberDetail.fulfilled, (s, a: PayloadAction<TeamMemberDetail>) => {
      s.loading = false;
      s.detail = a.payload;
    });
    b.addCase(fetchTeamMemberDetail.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error.message ?? "Failed to load";
    });

    b.addCase(fetchUserDocuments.pending, (s) => {
      s.docLoading = true;
    })
      .addCase(fetchUserDocuments.fulfilled, (s, a: PayloadAction<ReadAllResponse<DocumentRecord>>) => {
        s.docLoading = false;
        s.documents = a.payload.data;
        s.docTotal = a.payload.count ?? 0;
      })
      .addCase(fetchUserDocuments.rejected, (s) => {
        s.docLoading = false;
      });

    b.addCase(updateTeamMember.fulfilled, (s, a: PayloadAction<TeamMember>) => {
      if (s.detail) s.detail.member = a.payload;
    });

    b.addCase(refetchTeamMember.fulfilled, (s, a: PayloadAction<TeamMember>) => {
      if (s.detail?.member.id === a.payload.id) s.detail.member = a.payload;
    });

    b.addCase(uploadUserDocument.fulfilled, (s, a: PayloadAction<DocumentRecord>) => {
      s.documents.unshift(a.payload);
    });

    b.addCase(deleteUserDocument.fulfilled, (s, a) => {
      s.documents = s.documents.filter((d) => d.id !== a.payload);
    });
  },
});

export const { clearDetail, setUploadDocumentModalOpen, setDocPage, setDocPageSize } = slice.actions;
export default slice.reducer;
